import { FolderOpen, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { projectService } from "@/lib/services";
import type { ProjectResponse } from "@/types";

interface ProjectContextBarProps {
  /** UUID of the project the current chat is scoped to. */
  projectUuid: string;
  /** Called when the user explicitly leaves the project (starts a fresh, non-project chat). */
  onLeave: () => void;
}

/**
 * Persistent bar shown at the top of the Chat page whenever the current (or
 * about-to-be-created) chat session is scoped to a project. Gives the chat
 * UI a visible sense of place — clicking the project name returns to the
 * Project Hub — and an explicit way to step out of the project context
 * rather than losing it silently.
 */
export function ProjectContextBar({ projectUuid, onLeave }: Readonly<ProjectContextBarProps>) {
  const navigate = useNavigate();
  const [project, setProject] = useState<ProjectResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    setProject(null);
    projectService
      .get(projectUuid)
      .then((res) => {
        if (!cancelled) setProject(res.data);
      })
      .catch((err) => {
        console.error("Failed to load project context:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [projectUuid]);

  const accentColor = project?.color || "#7C16F5";

  return (
    <div
      className="flex items-center justify-between gap-3 px-4 py-2.5 border-b-2 shadow-xs"
      style={{ backgroundColor: `${accentColor}14`, borderColor: `${accentColor}66` }}
    >
      <button
        onClick={() => navigate(`/projects/${projectUuid}`)}
        className="flex items-center gap-3 min-w-0 text-left group"
        title="Back to project"
      >
        <div
          className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${accentColor}2b` }}
        >
          <FolderOpen className="h-5 w-5" style={{ color: accentColor }} />
        </div>
        <div className="min-w-0">
          <p
            className="text-[10px] font-bold uppercase tracking-wider leading-none mb-0.5"
            style={{ color: accentColor }}
          >
            Project
          </p>
          <p className="text-sm font-semibold text-content-primary group-hover:underline truncate leading-tight">
            {project?.name || "Project"}
          </p>
        </div>
        {project?.status === "archived" && (
          <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-gray-500/10 text-gray-400 shrink-0">
            Archived
          </span>
        )}
      </button>
      <button
        onClick={onLeave}
        className="p-1.5 rounded-md text-content-tertiary hover:text-content-primary hover:bg-black/10 transition-colors shrink-0"
        title="Leave project (start a new chat outside this project)"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
